import { create } from 'zustand';
import api, { setTokens, setTenant } from '../lib/axios';

export interface UserSummary {
  id: string;
  email: string;
  name?: string;
  role?: string;
  superAdmin?: boolean;
}

export interface TenantSummary {
  id?: string;
  slug: string;
  name: string;
  role?: string;
}

interface AuthState {
  user: UserSummary | null;
  tenant: TenantSummary | null;
  tenants: TenantSummary[];
  needsTenantSelection: boolean;
  initialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  selectTenant: (slug: string) => Promise<void>;
  loadSession: () => Promise<void>;
  logout: () => void;
}

interface AuthResponse {
  accessToken?: string | null;
  refreshToken?: string | null;
  user: UserSummary | null;
  tenant?: TenantSummary | null;
  tenants?: TenantSummary[];
  needsTenantSelection?: boolean;
}

type SetState = (partial: Partial<AuthState>) => void;

/** Applies a login/Google/switch response to the store and token storage. */
function applyAuthResponse(set: SetState, data: AuthResponse): void {
  setTokens(data.accessToken ?? null, data.refreshToken ?? null);
  if (data.needsTenantSelection) {
    setTenant(null);
    set({
      user: data.user,
      tenant: null,
      tenants: data.tenants || [],
      needsTenantSelection: true,
    });
    return;
  }
  setTenant(data.tenant?.slug ?? null);
  set({
    user: data.user,
    tenant: data.tenant ?? null,
    tenants: data.tenants || [],
    needsTenantSelection: false,
  });
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tenant: null,
  tenants: [],
  needsTenantSelection: false,
  initialized: false,

  login: async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    applyAuthResponse(set, data);
  },

  loginWithGoogle: async (credential) => {
    const { data } = await api.post('/auth/google', { credential });
    applyAuthResponse(set, data);
  },

  selectTenant: async (slug) => {
    const { data } = await api.post('/auth/switch-tenant', { tenantSlug: slug });
    setTokens(data.accessToken, data.refreshToken);
    setTenant(data.tenant?.slug ?? slug);
    set({ tenant: data.tenant ?? null, needsTenantSelection: false });
  },

  loadSession: async () => {
    if (!localStorage.getItem('hissab.access')) {
      set({ initialized: true });
      return;
    }
    try {
      const { data } = await api.get('/auth/session');
      if (data.tenant?.slug) {
        setTenant(data.tenant.slug);
      }
      set({
        user: data.user ?? null,
        tenants: data.tenants || [],
        tenant: data.tenant ?? null,
        needsTenantSelection:
          !data.tenant && !data.user?.superAdmin && (data.tenants?.length ?? 0) !== 1,
        initialized: true,
      });
    } catch {
      setTokens(null, null);
      setTenant(null);
      set({
        user: null,
        tenant: null,
        tenants: [],
        needsTenantSelection: false,
        initialized: true,
      });
    }
  },

  logout: () => {
    api.post('/auth/logout').catch(() => undefined);
    setTokens(null, null);
    setTenant(null);
    set({ user: null, tenant: null, tenants: [], needsTenantSelection: false });
  },
}));

/** True when the user is a platform super-admin or holds the SUPER_ADMIN tenant role. */
export const useIsSuperAdmin = (): boolean =>
  useAuthStore((s) => {
    if (s.user?.superAdmin) {
      return true;
    }
    const slug = s.tenant?.slug;
    const membership = s.tenants.find((tenant) => tenant.slug === slug);
    return (membership?.role ?? s.tenant?.role) === 'SUPER_ADMIN';
  });

/** True when the user administers the current organization (ADMIN or SUPER_ADMIN). */
export const useIsTenantAdmin = (): boolean =>
  useAuthStore((s) => {
    if (s.user?.superAdmin) {
      return true;
    }
    const slug = s.tenant?.slug;
    const membership = s.tenants.find((tenant) => tenant.slug === slug);
    const role = membership?.role ?? s.tenant?.role;
    return role === 'ADMIN' || role === 'SUPER_ADMIN';
  });
